import type { DscApplicantSection } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import { type Resolver, useFieldArray, useForm, useFormState } from "react-hook-form";

import { mapBankSectionToBankAccount } from "./bankMapping";
import { mapIncomeSectionToIncomes } from "./incomeMapping";
import { deriveApplicantSsnType, mapResidenceSectionToCustomerInfo } from "./residenceMapping";
import { buildSignersSchema, type SignersInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// useSignersForm
//
// Owns all RHF state for the Signers card. Returns the form instance and the
// two field-array instances (driver + secondSigner incomes) so Signers.tsx and
// SignerColumn stay presenter-only.
//
// Presence is derived from `present` in the dashboard response. Income blocks
// are hydrated from each applicant's `income.employments` (see incomeMapping);
// an applicant with no employments seeds exactly ONE blank income block. The
// bank account is hydrated from `bank.bankAccounts` (see bankMapping), and
// customer info from `residence` (see residenceMapping).
// ---------------------------------------------------------------------------

interface UseSignersFormArguments {
  /** Primary applicant (driver) section from the dashboard response. */
  driver?: DscApplicantSection;
  /** Second signer (cosigner) section from the dashboard response. */
  secondSigner?: DscApplicantSection;
  /** Whether Bank Name validation is optional (GOLD/PLATINUM scoring programs — OR-23394). */
  isBankNameOptional?: boolean;
}

/**
 * @param {UseSignersFormArguments} args - Driver/2nd-signer sections from the dashboard response.
 * @returns {object} RHF form instance and the driver/2nd-signer income field-array helpers.
 */
export function useSignersForm({ driver, secondSigner, isBankNameOptional = false }: UseSignersFormArguments = {}) {
  const hasSecondSigner = Boolean(secondSigner?.present);

  // SSN/ITIN status per applicant — gates each column's Income Type list and
  // clamps hydrated income types (see incomeMapping / IncomeBlock).
  const driverSsnType = deriveApplicantSsnType(driver?.residence);
  const secondSignerSsnType = deriveApplicantSsnType(secondSigner?.residence);

  const zodSignersResolver = zodResolver(buildSignersSchema(isBankNameOptional)) as unknown as Resolver<SignersInput>;

  /**
   * Maps the current driver/2nd-signer dashboard sections to form values.
   * Shared by the initial `defaultValues` and the resync effect below, so a
   * dashboard refetch (e.g. after a Call Script save) maps the same way a
   * fresh mount would.
   * @returns {SignersInput} The mapped form values.
   */
  function buildFormValues(): SignersInput {
    return {
      driver: {
        incomes: mapIncomeSectionToIncomes(driver?.income, driverSsnType),
        bankAccount: mapBankSectionToBankAccount(driver?.bank),
        customerInfo: mapResidenceSectionToCustomerInfo(driver?.residence),
      },
      // Hydrate the 2nd signer only when the response says one is present;
      // otherwise leave it undefined (matches `personSchema.optional()`) so
      // `incomes.min(1)` validation doesn't run against a hidden column.
      secondSigner: hasSecondSigner
        ? {
            incomes: mapIncomeSectionToIncomes(secondSigner?.income, secondSignerSsnType),
            bankAccount: mapBankSectionToBankAccount(secondSigner?.bank),
            customerInfo: mapResidenceSectionToCustomerInfo(secondSigner?.residence),
          }
        : undefined,
    } as SignersInput;
  }

  const form = useForm<SignersInput>({
    // zodResolver infers the schema's INPUT type: because several fields use
    // .default() and the schema is a ZodEffects (via .superRefine), defaulted
    // fields become `string | undefined` and diverge from SignersInput (the
    // output type the form is typed with). Cast to the form's resolver type —
    // runtime behavior is unchanged (it still validates and returns output).
    //
    // `secondSignerIncomes` below mounts a field array on "secondSigner.incomes"
    // unconditionally (rules of hooks), which makes RHF synthesize
    // `secondSigner: { incomes: [] }` in form state even when there's no 2nd
    // signer — failing `personSchema.optional()` on every submit. Strip it back
    // to undefined before Zod sees it so a driver-only deal can still save.
    resolver: (values, context, options) =>
      zodSignersResolver(hasSecondSigner ? values : { ...values, secondSigner: undefined }, context, options),
    mode: "onBlur",
    defaultValues: buildFormValues(),
  });

  const { control, setValue } = form;

  // Both field arrays are always mounted (React hook rules); the parent decides
  // which columns to render based on the response.
  const driverIncomes = useFieldArray({
    control,
    name: "driver.incomes",
  });

  const secondSignerIncomes = useFieldArray({
    control,
    name: "secondSigner.incomes",
  });

  /**
   * Pushes `incomes` into the field array in place (per-index `setValue`,
   * never `reset`/`replace`) when the fresh row count matches what's
   * currently rendered. A mismatch (e.g. an appended-but-unsaved row) is
   * left untouched entirely — shared by the driver/secondSigner resync below
   * so both sides apply (and gate) the same way.
   * @param {"driver" | "secondSigner"} pathPrefix - Which person's incomes to sync.
   * @param {SignersInput["driver"]["incomes"]} incomes - The freshly mapped incomes.
   * @param {number} currentFieldCount - The field array's current row count.
   * @returns {boolean} Whether the sync actually applied (the count matched).
   */
  function syncIncomesIfCountMatches(
    pathPrefix: "driver" | "secondSigner",
    incomes: SignersInput["driver"]["incomes"],
    currentFieldCount: number
  ): boolean {
    if (incomes.length !== currentFieldCount) {
      return false;
    }
    for (const [index, income] of incomes.entries()) {
      setValue(`${pathPrefix}.incomes.${index}`, income, { shouldDirty: false });
    }
    return true;
  }

  // `form.formState.isDirty` is only kept live for properties actually read
  // through a subscribed hook (useForm's own render-time read, or
  // useFormState) — react-hook-form's formState Proxy never recomputes a
  // property nothing has "subscribed" to, so a plain `form.formState.isDirty`
  // read from the effect below would always see its initial `false`. This
  // subscribes to it properly.
  const { isDirty } = useFormState({ control });

  // Re-syncs the form when the dashboard data itself changes (e.g. a Call
  // Script save invalidates and refetches the dashboard query) — RHF's
  // `defaultValues` above only applies once, at mount, so without this the
  // form keeps showing whatever was hydrated the first time even after the
  // underlying dashboard data moves on. Skipped whenever the form has any
  // unsaved changes: a full-form `reset()` only preserves dirty *scalar*
  // fields — an appended-but-unsaved income row (a field-array item beyond
  // the incoming data's length) gets silently dropped, not preserved, so
  // it's safer to wait for a clean form than to risk losing an in-progress edit.
  //
  // Deliberately NOT `reset(buildFormValues())`: react-hook-form regenerates
  // every field-array row's internal id on every `reset()` call (confirmed —
  // it does this even when the row's own data is byte-identical), which
  // remounts each income row's component — silently closing that row's Call
  // Script modal, since its `open` state lives in that now-unmounted
  // component. `setValue` per path updates the same field-array entries in
  // place instead, so nothing under `driver.incomes`/`secondSigner.incomes`
  // ever remounts. customerInfo/bankAccount aren't field arrays, so they can
  // be set wholesale; incomes are only resynced when the row count matches
  // what's rendered (a raw count change mid-session is rare, and safely
  // skipped rather than risking a mismatched update).
  //
  // Content-compared (not reference-compared): `driver`/`secondSigner` are
  // reference-stable from React Query's structural sharing in production,
  // but a caller that doesn't memoize its props would otherwise re-trigger
  // this on every render — comparing the serialized content instead makes
  // the effect correct regardless of the caller's memoization.
  //
  // `lastSyncedSectionsReference` is only advanced once every part of this
  // content actually applied (see the incomes-length gate below) — otherwise
  // an unresolved mismatch could get permanently stamped "synced" the first
  // time it's seen, so a later render with the SAME content (e.g. after the
  // analyst removes the extra row that caused the mismatch) would short
  // -circuit at the top of this effect and never retry the sync it skipped.
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const lastSyncedSectionsReference = useRef<string | undefined>(undefined);
  useEffect(() => {
    const serializedSections = JSON.stringify([driver, secondSigner]);
    if (serializedSections === lastSyncedSectionsReference.current) {
      return;
    }
    if (lastSyncedSectionsReference.current === undefined) {
      // Initial mount — `defaultValues` above already covers it.
      lastSyncedSectionsReference.current = serializedSections;
      return;
    }
    if (isDirty) {
      // Retry on a later render once the form is no longer dirty, rather
      // than marking this content synced without ever applying it.
      return;
    }

    const fresh = buildFormValues();
    setValue("driver.customerInfo", fresh.driver.customerInfo, { shouldDirty: false });
    setValue("driver.bankAccount", fresh.driver.bankAccount, { shouldDirty: false });
    const driverIncomesSynced = syncIncomesIfCountMatches("driver", fresh.driver.incomes, driverIncomes.fields.length);

    let secondSignerIncomesSynced = true;
    if (fresh.secondSigner) {
      setValue("secondSigner.customerInfo", fresh.secondSigner.customerInfo, { shouldDirty: false });
      setValue("secondSigner.bankAccount", fresh.secondSigner.bankAccount, { shouldDirty: false });
      secondSignerIncomesSynced = syncIncomesIfCountMatches(
        "secondSigner",
        fresh.secondSigner.incomes,
        secondSignerIncomes.fields.length
      );
    }

    if (driverIncomesSynced && secondSignerIncomesSynced) {
      lastSyncedSectionsReference.current = serializedSections;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when the dashboard sections' content (or dirty state, gating it) changes; buildFormValues/setValue/syncIncomesIfCountMatches/the field arrays close over stable-enough deps for this
  }, [driver, secondSigner, isDirty]);

  return {
    form,
    control,
    driverIncomes,
    secondSignerIncomes,
    driverSsnType,
    secondSignerSsnType,
  };
}
